import { Avatar, NumberInput, Select, Text } from "@mantine/core"
import { ImageIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { ILinePlus } from '../../models/Line'
import { IAuth } from '../../store/Auth'
import { usePermission } from "../permission"
import { ColumnType, Table } from "../table"
import { BranchForm } from "./branch-form"
import { StaffForm } from "./staff-form"
import { renderBusinessName } from "../../utils/render-business-name"

export function StackLines({ list, values, setFieldValue }: { list: any; values: any; setFieldValue: any }) {
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const ORD_EDIT = usePermission("ORD_EDIT")
  const { t } = useTranslation(["order", "common"])

  const columns = useHeader({ t, values, setFieldValue, user, ORD_EDIT })

  return <Table name="stack-lines.list" columns={columns} dataSource={(list || []).LinesData} pagination={false} />
}

const useHeader = ({ t, values, setFieldValue, user, ORD_EDIT }: { t: any; values: any; setFieldValue: any; user: any; ORD_EDIT: any }): ColumnType<ILinePlus>[] => [
  {
    title: "#",
    render: (_r, index) => <Text size="sm">{index + 1}</Text>,
    width: "80px",
  },
  {
    title: t("order:purchaseSales.image"),
    dataIndex: "image",
    render: (record) => (
      <Avatar src={record?.image || null} radius="xs">
        <ImageIcon />
      </Avatar>
    ),
  },
  {
    title: t("order:purchaseSales.skuCode"),
    dataIndex: "skuCode",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.skuCode ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.productService"),
    dataIndex: "name",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.name ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.unit"),
    dataIndex: "unit",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.unit ?? "-"}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.totalOrderedQuantity"),
    dataIndex: "totalOrderedQuantity",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.totalOrderedQuantity ?? 0}
        </Text>
      )
    },
  },
  {
    title: "Qty_Available_Sale",
    dataIndex: "availableQuantity",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.availableQuantity ?? 0}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.remaining"),
    dataIndex: "minQuantity",
    render: (record) => {
      return (
        <Text size="sm" c="var(--mantine-color-dark-5)" w="max-content">
          {record?.minQuantity ?? 0}
        </Text>
      )
    },
  },
  {
    title: t("order:purchaseSales.orderQuantity"),
    dataIndex: "quantity",
    render: (_record, index) => {
      return (
      <NumberInput
        max={9000000}
        min={0}
        size="sm"
        hideControls
        value={values?.lines?.[index]?.quantity ?? ""}
        onChange={(v) => setFieldValue(`lines.${index}.quantity`, v)}
        placeholder={t("order:purchaseSales.quantityPlaceholder")}
      />
    )
    },
  },
  {
    title: t("order:purchaseSales.buyer"),
    dataIndex: "buyerBusinessId",
    render: (record, index) => {
      if (record?.buyers) {
        return (
          <Select
            name={`lines[${index}].buyerBusinessId`}
            size="sm"
            value={values?.lines?.[index]?.buyerBusinessId ?? null}
            onChange={(v) => {
              setFieldValue(`lines.${index}.buyerBusinessId`, v)
              setFieldValue(`lines.${index}.variantId`, record.variantId)
            }}
            data={(record?.buyers || []).map((c: any) => ({
              label: renderBusinessName(c, ""),
              value: c.id,
            }))}
            clearable
            placeholder={t("common:actions.select")}
          />
        )
      } else {
        return <>-</>
      }
    },
  },
  {
    title: t("order:purchaseSales.supplierPartner"),
    dataIndex: "supplierBusinessId",
    render: (record, index) => {
      if (record?.suppliers) {
        return (
          <Select
            name={`lines[${index}].supplierBusinessId`}
            size="sm"
            value={values?.lines?.[index]?.supplierBusinessId ?? null}
            onChange={(v) => setFieldValue(`lines.${index}.supplierBusinessId`, v)}
            data={(record?.suppliers || []).map((c: any) => ({
              label: renderBusinessName(c, ""),
              value: c.id,
            }))}
            clearable
            placeholder={t("common:actions.select")}
          />
        )
      } else {
        return "-"
      }
    },
  },
  {
    title: t("order:purchaseSales.receiverStaff"),
    dataIndex: "unit.name",
    render: (_record, index) => {
      return (
        <>
          {ORD_EDIT.hasAccess && (
            <>
              {!values?.lines || !values?.lines[index]?.buyerBusinessId ? (
                <Text size="sm" c="var(--mantine-color-dark-5)">
                  {"-"}
                </Text>
              ) : (
                <StaffForm
                  id={values?.lines || (values?.lines[index]?.buyerBusinessId && values?.lines[index]?.buyerBusinessId)}
                  values={values}
                  setFieldValue={setFieldValue}
                  index={index}
                />
              )}
            </>
          )}
        </>
      )
    },
  },
  {
    title: t("order:purchaseSales.receiverBranch"),
    dataIndex: "unit.name",
    render: (_record, index) => {
      return (
        <>
          {!values?.lines || !values?.lines[index]?.buyerBusinessId ? (
            <Text size="sm" c="var(--mantine-color-dark-5)">
              {"-"}
            </Text>
          ) : (
            <BranchForm id={values?.lines || (values?.lines[index]?.buyerBusinessId && values?.lines[index]?.buyerBusinessId)} values={values} setFieldValue={setFieldValue} index={index} />
          )}
        </>
      )
    },
  },
]
